import {
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Select,
  Stack,
  styled,
  Tooltip,
  Typography
} from '@mui/material';
import { Box } from '@mui/system';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
// material icons
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useDispatch } from 'react-redux';
import CloseIcon from '@mui/icons-material/Close';
import InterestsIcon from '@mui/icons-material/Interests';
import LinkIcon from '@mui/icons-material/Link';
// react icons
import { BiCommentDetail, BiShareAlt } from 'react-icons/bi';
import { BsTelephone } from 'react-icons/bs';
import { CgCalendarDates } from 'react-icons/cg';
import { FaRegUser } from 'react-icons/fa';
import { GiModernCity, GiSwapBag } from 'react-icons/gi';
import { HiOutlineMail } from 'react-icons/hi';
import { MdAppRegistration, MdEmail, MdTextsms, MdUpdate } from 'react-icons/md';
import { RiWhatsappFill } from 'react-icons/ri';
// components
import BrudCrumbs from '../../components/BreadCrumbs';
import Page from '../../components/Page';
import PageTitle from '../../components/PageHeading';
import Scrollbar from '../../components/Scrollbar';
import { fDate, fDateTime } from '../../utils/formatTime';
import { NOT_AVAILABLE } from '../Global/Constants';
import { formatToYYYYMMDD, isValidValue, normalizeValue } from '../../utils/CommonFunction';
import ViewCardComponent from '../../components/ViewCards';
import CustomTableComponent from '../../components/CustomTable';
import useLoginSelector from '../../store/Slice/Login/LoginSelector';
import CustomSelect from '../../components/CustomSelect';
import CustomInput from '../../components/CustomInput';
import CustomDialog from '../../components/CustomDialogue';
import useLeadSelector from '../../store/Slice/Lead/LeadSelector';
import {
  getLeadAddComment,
  getLeadAddRemainder,
  getLeadViewData
} from '../../store/Slice/Lead/LeadSlice';
import { getInternalSaleManager, getMasterListType } from '../../store/Slice/Global/GlobalSlice';
import useGlobalSelector from '../../store/Slice/Global/GlobalSelector';

const StyledIconButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.common.white,
  margin: '0 3px',
  [`&:hover`]: {
    color: theme.palette.primary.main
  }
}));

const validationSchema = yup.object().shape({
  taskId: yup.string().nullable().required('Task is required'), // allows null but still required
  assignedTo: yup.string().nullable().required('Assign To is required'),
  comments: yup
    .string()
    .nullable()
    .required('Comments are required')
    .min(5, 'Comments must be at least 5 characters')
});

const remainderSchema = yup.object().shape({
  reminderDate: yup
    .date()
    .nullable()
    .transform((value, originalValue) => (originalValue === '' ? null : value))
    .required('Reminder Date is required')
    .min(new Date(new Date().setHours(0, 0, 0, 0)), 'Reminder Date cannot be in the past'),
  reminderTime: yup.string().nullable().required('Reminder Time is required'),
  comments: yup
    .string()
    .nullable()
    .required('Comments are required')
    .min(5, 'Comments must be at least 5 characters')
});

function ActivityLeads() {
  const [selectURL, setSelectURL] = useState(' ');
  const [sharePopUp, setSharePopUp] = useState(false);
  const [isActive, setActive] = useState(false);
  const [errorHandling, setErrorHandling] = useState({ open: false, message: null });
  const [isEdit, setIsEdit] = useState(false); // show comment
  const [isAddRemainder, setIsAddRemainder] = useState(false); // show remainder

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { leadesItem } = location?.state || {};

  const { LoginData } = useLoginSelector();
  const { MasterTypeListDrop, InternalSaleManagerFilterDrop } = useGlobalSelector();
  const { LeadViewData } = useLeadSelector();
  const activityLog = LeadViewData?.leadDetailsActivityLogResponseModel ?? {};
  const appointment = LeadViewData?.leadDetailsAppointmentDetailsResponseModel ?? null;
  const currentStatus = LeadViewData?.leadDetailsCurrentStatusResponseModel ?? {};
  const followUp = LeadViewData?.leadDetailsFollowUpDetailsResponseModel ?? {};
  const loanReq = LeadViewData?.leadDetailsLoanRequirementResponseModel ?? {};
  const reminders = LeadViewData?.leadDetailsReminderResponseModel ?? {};

  const {
    handleSubmit: handleReduxSubmit,
    control,
    reset,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(validationSchema) // call from add edit prospect
  });

  const {
    handleSubmit: handleRemainderSubmit,
    control: remainderControl,
    reset: resetRemainder,
    formState: { errors: remainderError }
  } = useForm({
    resolver: yupResolver(remainderSchema) // call from add edit prospect
  });

  const callLogColumns = [
    { label: 'Date', key: 'callDateTime', formatter: fDate },
    { label: 'Mode', key: 'modeTypeName' },
    { label: 'Status', key: 'callStatus' },
    { label: 'Comments', key: 'callComments' }
  ];

  const commentLogColumns = [
    { label: 'Date', key: 'assignedOn', formatter: fDateTime },
    { label: 'Comment By', key: 'assignedByName' },
    { label: 'Comment', key: 'comment' }
  ];

  const toggleClass = (box) => {
    setActive(box);
  };

  // show edit card
  const handleEditCard = () => {
    reset();
    setIsEdit(true);
  };

  // show remainder card
  const handleRemainderCard = () => {
    resetRemainder();
    setIsAddRemainder(true);
  };

  // close edit card
  const handleCloseCard = (event) => {
    setIsEdit(false);
    setIsAddRemainder(false);
  };

  // edit submit api
  const onSubmit = useCallback(
    async (data) => {
      const finalData = {
        ...data,
        leadId: leadesItem?.[0]?.leadId,
        companyId: LoginData?.companyId,
        assignedBy: LoginData?.userId
      };

      try {
        const res = await dispatch(getLeadAddComment(finalData));
        if (res?.status === 'success') {
          getLeadViewApi();
          handleCloseCard();
        }
      } catch (error) {
        console.error('Error submitting form:', error);
      }
    },
    [LoginData, leadesItem] // 🔹 depends on LoginData
  );

  // remainder submit api
  const onRemainderSubmit = useCallback(
    async (data) => {
      const finalData = {
        ...data,
        reminderDate: formatToYYYYMMDD(data?.reminderDate),
        leadId: leadesItem?.[0]?.leadId,
        companyId: LoginData?.companyId,
        userId: LoginData?.userId
      };

      try {
        const res = await dispatch(getLeadAddRemainder(finalData));
        if (res?.status === 'success') {
          getLeadViewApi();
          handleCloseCard();
        }
      } catch (error) {
        console.error('Error submitting form:', error);
      }
    },
    [LoginData, leadesItem] // 🔹 depends on LoginData
  );

  // internal sale manager list
  const getInternalSaleManagerData = useCallback(() => {
    if (!LoginData?.companyId) return; // safety check
    const data = {
      companyId: LoginData?.companyId
    };
    dispatch(getInternalSaleManager(data));
  }, [LoginData]);

  // master type data
  const MasterTypeApi = () => {
    const type = 'LEAD_COMMENT_TASK_TYPE';
    dispatch(getMasterListType(type));
  };

  // lead view data
  const getLeadViewApi = useCallback(() => {
    const credentials = {
      companyId: LoginData?.companyId,
      userId: LoginData?.userId,
      subOrdinateId: leadesItem?.[0]?.assignedTo,
      leadId: leadesItem?.[0]?.leadId
    };
    dispatch(getLeadViewData(credentials));
  }, [LoginData, leadesItem]);

  useEffect(() => {
    getLeadViewApi(); // lead view data
    MasterTypeApi();
    getInternalSaleManagerData();
  }, []);

  const loanRequirementFields = useMemo(() => {
    const model = loanReq;

    if (!model) return [];

    return [
      {
        label: 'Name',
        value: normalizeValue(model?.leadName, NOT_AVAILABLE),
        icon: <FaRegUser className="viewLabelIcon" size={24} />
      },
      {
        label: 'Phone',
        value: normalizeValue(model?.leadMobileNumber, NOT_AVAILABLE),
        icon: <BsTelephone className="viewLabelIcon" size={24} />
      },
      {
        label: 'Loan Type',
        value: normalizeValue(model?.loanType, NOT_AVAILABLE),
        icon: <InterestsIcon className="viewLabelIcon" size={24} />
      },
      {
        label: 'Employment Type',
        value: normalizeValue(model?.employmentType, NOT_AVAILABLE),
        icon: <InterestsIcon className="viewLabelIcon" size={24} />
      },
      {
        label: 'Loan Amount',
        value: normalizeValue(model?.loanAmount, NOT_AVAILABLE),
        icon: <GiSwapBag className="viewLabelIcon" size={24} />
      },
      {
        label: 'City',
        value: normalizeValue(model?.cityName, NOT_AVAILABLE),
        icon: <GiModernCity className="viewLabelIcon" size={24} />
      },
      {
        label: 'Email',
        value: normalizeValue(model?.leadEmail, NOT_AVAILABLE),
        icon: <HiOutlineMail className="viewLabelIcon" size={24} />
      }
    ];
  }, [loanReq]);

  const currentStatusFields = useMemo(() => {
    const model = currentStatus;

    if (!model) return [];

    return [
      {
        label: 'Lead ID / Application ID',
        value: `${normalizeValue(model?.leadId, NOT_AVAILABLE)}${
          model?.applicationId ? ` / ${model?.applicationId}` : ''
        }`,
        icon: <MdAppRegistration className="viewLabelIcon" size={24} />
      },
      {
        label: 'Application URL',
        value: normalizeValue(model?.applicationUrl, NOT_AVAILABLE),
        icon: <LinkIcon className="viewLabelIcon" size={24} />
      },
      {
        label: 'Document URL',
        value: normalizeValue(model?.documentUrl, NOT_AVAILABLE),
        icon: <LinkIcon className="viewLabelIcon" size={24} />
      },
      {
        label: 'Verification URL',
        value: normalizeValue(model?.verificationUrl, NOT_AVAILABLE),
        icon: <LinkIcon className="viewLabelIcon" size={24} />
      }
    ];
  }, [currentStatus]);

  const callLogData = activityLog?.leadDetailsCallHistoryResponseModel?.callLogs || [];

  const commentLogData =
    activityLog?.leadDetailsCommentLogDetailsResponseModel
      ?.leadDetailsCommentLogDetailsResponseModels || [];

  return (
    <Page title="Partner Management System || Leads">
      <Container maxWidth="xl">
        <Box pt={3} pb={3} mb={4}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            className="pageHeading"
          >
            <PageTitle info="Application Progress Report" />
            <Box>
              <BrudCrumbs />
            </Box>
          </Stack>

          <Scrollbar>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box className="statusBox">
                <Typography variant="body2">Completion:</Typography>
                <Typography variant="subtitle2" className="ml-1">
                  60%
                </Typography>
              </Box>
              <Box mb={2} display="flex" gap={1}>
                <Tooltip
                  title="Update"
                  placement="top"
                  arrow
                  onClick={(e) =>
                    navigate(`/leads/activity/update`, { state: { leadesItem: leadesItem } })
                  }
                >
                  <IconButton
                    sx={{
                      border: '1px solid #eaedf1',
                      borderRadius: '4px',
                      backgroundColor: '#fff',
                      '&:hover': { backgroundColor: '#f4f6f8' }
                    }}
                  >
                    <MdUpdate size={18} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Share" placement="top" arrow>
                  <IconButton
                    sx={{
                      border: '1px solid #eaedf1',
                      borderRadius: '4px',
                      backgroundColor: '#fff',
                      '&:hover': { backgroundColor: '#f4f6f8' }
                    }}
                    onClick={(e) => setSharePopUp(true)}
                  >
                    <BiShareAlt size={18} />
                  </IconButton>
                </Tooltip>
                <Tooltip
                  title="Set Reminder"
                  placement="top"
                  arrow
                  onClick={(e) => handleRemainderCard()}
                >
                  <IconButton
                    sx={{
                      border: '1px solid #eaedf1',
                      borderRadius: '4px',
                      backgroundColor: '#fff',
                      '&:hover': { backgroundColor: '#f4f6f8' }
                    }}
                  >
                    <CgCalendarDates size={18} />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            <Grid container spacing={3} mb={3} alignItems="stretch">
              {loanRequirementFields.length > 0 && (
                <ViewCardComponent title="Loan Requirement" fields={loanRequirementFields} />
              )}

              {currentStatusFields.length > 0 && (
                <ViewCardComponent title="Current Status" fields={currentStatusFields} />
              )}
            </Grid>

            {isValidValue(appointment?.address) &&
              isValidValue(appointment?.appointmentDate) &&
              isValidValue(appointment?.appointmentTime) && (
                <Box className="viewLeadCard grayBGCard redColor" mb={3}>
                  <Typography variant="subtitle2">
                    {`Your Appointment to visit the customer Address ${normalizeValue(
                      appointment?.address,
                      NOT_AVAILABLE
                    )} has been set for ${normalizeValue(
                      appointment?.appointmentDate,
                      NOT_AVAILABLE
                    )} at ${normalizeValue(appointment?.appointmentTime, NOT_AVAILABLE)}.`}
                  </Typography>
                </Box>
              )}

            {isValidValue(followUp?.followUpDate) && isValidValue(followUp?.followUpTime) && (
              <Box className="viewLeadCard grayBGCard redColor" mb={3}>
                <Typography variant="subtitle2">
                  {`Follow up Call has been scheduled ${followUp?.followUpDate} at ${followUp?.followUpTime}.`}
                </Typography>
              </Box>
            )}

            {Array.isArray(reminders?.leadDetailsReminderDetailsResponseModels) &&
              reminders.leadDetailsReminderDetailsResponseModels.length > 0 &&
              reminders.leadDetailsReminderDetailsResponseModels.map((item, index) => (
                <Box key={index} className="viewLeadCard grayBGCard redColor" mb={3}>
                  <Typography variant="subtitle2">
                    {`Reminder is set for ${
                      isValidValue(item?.reminderDate) ? fDate(item.reminderDate) : NOT_AVAILABLE
                    } at ${normalizeValue(item?.reminderTime, NOT_AVAILABLE)}: ${normalizeValue(
                      item?.reminderComments,
                      NOT_AVAILABLE
                    )}`}
                  </Typography>
                </Box>
              ))}

            <Grid container spacing={3} mb={3}>
              <ViewCardComponent title="Activity Log">
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 2
                  }}
                >
                  <Box className="statusBox">
                    <Typography variant="body2">Current Status:</Typography>
                    <Typography variant="subtitle2" className="disbursedText">
                      {normalizeValue(activityLog?.leadStatus, NOT_AVAILABLE)}
                    </Typography>
                  </Box>
                  <Tooltip title="Add Comments" placement="top" arrow>
                    <IconButton
                      sx={{
                        border: '1px solid #eaedf1',
                        borderRadius: '4px',
                        backgroundColor: '#fff',
                        '&:hover': { backgroundColor: '#f4f6f8' }
                      }}
                      onClick={(e) => handleEditCard()}
                    >
                      <BiCommentDetail size={18} />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Box className="timelineB">
                  {activityLog?.leadDetailsLeadHistoryResponseModel
                    ?.leadDetailsLeadHistoryStageResponseModels?.length > 0 &&
                    activityLog.leadDetailsLeadHistoryResponseModel.leadDetailsLeadHistoryStageResponseModels?.map(
                      (item, index) => (
                        <Box key={index} className="timelineContentB timelineContentBGreen">
                          <Typography variant="subtitle1" className="date">
                            {normalizeValue(item?.stageName, NOT_AVAILABLE)}
                          </Typography>
                          <Typography variant="body2">
                            Start Date:{' '}
                            <strong>
                              {isValidValue(item?.startDate)
                                ? fDate(item.startDate)
                                : NOT_AVAILABLE}
                            </strong>
                          </Typography>
                          <Typography variant="body2" mb={1}>
                            End Date:{' '}
                            <strong>
                              {isValidValue(item?.endDate) ? fDate(item.endDate) : NOT_AVAILABLE}
                            </strong>
                          </Typography>
                        </Box>
                      )
                    )}
                </Box>
              </ViewCardComponent>
            </Grid>

            <Grid container spacing={3} mb={3}>
              <ViewCardComponent title="Call Log">
                <Box sx={{ mt: 1 }}>
                  <CustomTableComponent columns={callLogColumns} data={callLogData} />
                </Box>
              </ViewCardComponent>
            </Grid>

            <Grid container spacing={3} mb={3}>
              <ViewCardComponent title="Comment Log">
                <Box sx={{ mt: 1 }}>
                  <CustomTableComponent columns={commentLogColumns} data={commentLogData} />
                </Box>
              </ViewCardComponent>
            </Grid>
          </Scrollbar>
        </Box>

        <Dialog
          open={sharePopUp}
          onClose={(e) => setSharePopUp(false)}
          fullWidth
          PaperProps={{ sx: { borderRadius: '16px' } }}
        >
          <DialogTitle
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              fontWeight: 700,
              fontSize: '1.1rem'
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Sharing URL
            </Typography>
            <IconButton onClick={(e) => setSharePopUp(false)} size="small">
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            <Box className="displayFlex alignItemCenter justifyContentCenter" mb={2}>
              <Box
                className={isActive === 'whatsapp' ? 'mailSMSType mr-2 active' : 'mailSMSType mr-2'}
                onClick={(e) => toggleClass('whatsapp')}
              >
                <RiWhatsappFill color="#2CB742" size={48} />
              </Box>
              <Box
                className={isActive === 'email' ? 'mailSMSType mr-2 active' : 'mailSMSType mr-2'}
                onClick={(e) => toggleClass('email')}
              >
                <MdEmail color="#0d4689" size={48} />
              </Box>
              <Box
                className={isActive === 'sms' ? 'mailSMSType mr-2 active' : 'mailSMSType mr-2'}
                onClick={(e) => toggleClass('sms')}
              >
                <MdTextsms color="#4fbbc8" size={48} />
              </Box>
            </Box>
            <Select
              fullWidth
              size="small"
              inputProps={{
                className: `textInput customSelectBox`
              }}
              value={selectURL}
              onChange={(e) => setSelectURL(e.target.value)}
              sx={{ borderRadius: '8px' }}
            >
              <MenuItem value=" ">Select URL</MenuItem>
              <MenuItem value="Application URL">Application URL</MenuItem>
              <MenuItem value="Document URL">Document URL</MenuItem>
              <MenuItem value="Verification URL">Verification URL</MenuItem>
            </Select>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button
              onClick={(e) => setSharePopUp(false)}
              variant="outlined"
              sx={{
                borderRadius: '8px',
                textTransform: 'none',
                borderColor: '#e2e8f0',
                color: '#637381',
                '&:hover': { borderColor: '#cbd5e1', bgcolor: '#f8fafc' }
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={(e) => setSharePopUp(false)}
              variant="contained"
              sx={{
                bgcolor: '#13519d',
                borderRadius: '8px',
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
                boxShadow: '0 4px 14px rgba(19,81,157,0.25)',
                '&:hover': { bgcolor: '#0d3b75' }
              }}
            >
              Send
            </Button>
          </DialogActions>
        </Dialog>

        {/* add new comment start */}
        <CustomDialog
          showCloseIcon
          fullWidth
          open={isEdit}
          onClose={(event, reason) => {
            if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
              handleCloseCard();
            }
          }}
          title="Add New Comments"
          content={
            <Box mb={3}>
              <form id="addCommentsActivityLeads" onSubmit={handleReduxSubmit(onSubmit)}>
                <Grid container spacing={3} mb={3}>
                  <Grid item xs={12}>
                    <CustomSelect
                      name="taskId"
                      label="Task"
                      control={control}
                      errors={errors}
                      options={MasterTypeListDrop || []}
                      isRequired
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <CustomSelect
                      name="assignedTo"
                      label="Assign to"
                      control={control}
                      errors={errors}
                      options={InternalSaleManagerFilterDrop || []}
                      isRequired
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <CustomInput
                      name="comments"
                      type="textarea"
                      multiline
                      rows={2}
                      label="Comments"
                      control={control}
                      errors={errors}
                      isRequired
                    />{' '}
                  </Grid>
                </Grid>{' '}
              </form>{' '}
            </Box>
          }
          actions={
            <Box display="flex" justifyContent="flex-end" gap={2}>
              <Button
                variant="outlined"
                sx={{
                  borderRadius: '8px',
                  textTransform: 'none',
                  borderColor: '#e2e8f0',
                  color: '#637381',
                  '&:hover': { borderColor: '#cbd5e1', bgcolor: '#f8fafc' }
                }}
                onClick={handleCloseCard}
              >
                Cancel
              </Button>

              <Button
                type="submit"
                form="addCommentsActivityLeads"
                variant="contained"
                sx={{
                  bgcolor: '#13519d',
                  borderRadius: '8px',
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 3,
                  boxShadow: '0 4px 14px rgba(19,81,157,0.25)',
                  '&:hover': { bgcolor: '#0d3b75' }
                }}
              >
                Save
              </Button>
            </Box>
          }
        />
        {/* add new comment end */}

        {/* Set Remainder start */}
        <CustomDialog
          showCloseIcon
          fullWidth
          open={isAddRemainder}
          onClose={(event, reason) => {
            if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
              handleCloseCard();
            }
          }}
          title="Set Reminder"
          content={
            <Box mb={3}>
              <form id="addRemainder" onSubmit={handleRemainderSubmit(onRemainderSubmit)}>
                <Grid container spacing={3} mb={3}>
                  <Grid item xs={6}>
                    <CustomInput
                      name="reminderDate"
                      type="Date"
                      label="Date"
                      control={remainderControl}
                      errors={remainderError}
                      isRequired
                    />{' '}
                  </Grid>
                  <Grid item xs={6}>
                    <CustomInput
                      name="reminderTime"
                      type="Time"
                      label="Time"
                      control={remainderControl}
                      errors={remainderError}
                      isRequired
                    />{' '}
                  </Grid>
                  <Grid item xs={12}>
                    <CustomInput
                      name="comments"
                      type="textarea"
                      multiline
                      rows={2}
                      label="Comments"
                      control={remainderControl}
                      errors={remainderError}
                      isRequired
                    />{' '}
                  </Grid>
                </Grid>{' '}
              </form>{' '}
            </Box>
          }
          actions={
            <Box display="flex" justifyContent="flex-end" gap={2}>
              <Button
                variant="outlined"
                sx={{
                  borderRadius: '8px',
                  textTransform: 'none',
                  borderColor: '#e2e8f0',
                  color: '#637381',
                  '&:hover': { borderColor: '#cbd5e1', bgcolor: '#f8fafc' }
                }}
                onClick={handleCloseCard}
              >
                Cancel
              </Button>

              <Button
                type="submit"
                form="addRemainder"
                variant="contained"
                sx={{
                  bgcolor: '#13519d',
                  borderRadius: '8px',
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 3,
                  boxShadow: '0 4px 14px rgba(19,81,157,0.25)',
                  '&:hover': { bgcolor: '#0d3b75' }
                }}
              >
                Save
              </Button>
            </Box>
          }
        />
        {/* Set Remainder end */}

        <Dialog
          open={errorHandling.open}
          onClose={(e) => setErrorHandling({ open: false, message: null })}
          fullWidth
        >
          <DialogContent dividers>
            <Box className="" py={2}>
              {errorHandling.message}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={(e) => {
                setErrorHandling({ open: false, message: null });
              }}
              variant="contained"
              className="mainBtn"
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Page>
  );
}
export default ActivityLeads;
